"use client";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CgArrowBottomRight, CgArrowTopRight } from "react-icons/cg";
import { FaArrowTrendDown, FaArrowTrendUp, FaLightbulb } from "react-icons/fa6";
import { Layers2, Eye } from "lucide-react";
import { formatToDecimalPlaces } from "@/helpers/helperFunctions";
import { SharpRatioString } from "@/helpers/types";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { Button } from "@/components/ui/button";
import { Label } from "@radix-ui/react-label";
import { cn } from "@/lib/utils";
import { useTranslation } from "next-i18next";
import PriceLabel from "../common/PriceLable";
import { RxCross2 } from "react-icons/rx";
import HintLabel from "../common/HintLable";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import Marquee from "react-fast-marquee";
import StrategyName from "./StrategyName";
import Autoplay from "embla-carousel-autoplay";
import { AssetBadge } from "../common/AssetBadge";
export interface StrategyCardProps {
  item: any;
  index: number;
  logos: Record<string, string>;
  isLoadingTicker: boolean;
  isCurrentPriceLoading: boolean;
  onView: (data: any) => void;
  onExplain: (data: any) => void;
  onCopy: (item: any) => void;
  showExplain: boolean;
  animate?: boolean;
}

export default function StrategyCard({
  item,
  index,
  logos,
  isCurrentPriceLoading,
  onView,
  onExplain,
  onCopy,
  showExplain,
  animate,
}: StrategyCardProps) {
  const { t } = useTranslation("build-page");

  const badges: string[] = [
    item?.meta_data?.strategy_meta?.actions,
    item?.meta_data?.strategy_meta?.datas,
    item?.meta_data?.strategy_meta?.indicators,
    item?.meta_data?.strategy_meta?.logics,
  ].reduce((acc: string[], curr: string[]) => acc.concat(curr ?? []), []);

  const rawCanvas = item?.strategy?.canvas_data;
  const canvasData = rawCanvas ? JSON.parse(rawCanvas) : [];
  const nodes = canvasData?.filter((n: any) => n.type === "selectorNode") ?? [];
  const edges = canvasData?.filter((n: any) => n.type === "buttonedge") ?? [];

  const symbols_list = item?.meta_data?.symbol_list?.filter(
    (sym: string) => sym != item?.strategy?.symbol,
  );

  const category = Object.keys(
    item?.meta_data?.strategy_meta?.indicator_category || {},
  ).join(", ");

  const maxTakeProfit = item?.meta_data?.tpsl?.take_profit_p_max;
  const stopLoss = item?.meta_data?.tpsl?.stop_loss_p_max;

  let actions = new Set(item?.meta_data?.strategy_meta?.actions);
  const actionList = Array.from(actions) as string[];

  const actionOptions: Record<string, string> = {
    Buy: "Long",
    "Buy (Long)": "Long",
    Sell: "Short",
    "Sell (Short)": "Short",
    "Smart Buy": "Smart Buy",
    "Smart Sell": "Smart Sell",
    "Smart Exit Position": "Smart Exit",
  };

  return (
    <Card
      key={item?.strategy?.symbol + "_" + index}
      className={cn(
        "h-fit mt-4 flex flex-col gap-1",
        index !== 0 ? "mt-2" : "",
        animate &&
          "transition-all duration-500 hover:bg-blue-900/[0.08] hover:border-blue-400/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] hover:-translate-y-1",
      )}
    >
      {/* HEADER */}
      <CardHeader className="p-4">
        <div className="flex  flex-row justify-between gap-4">
          {/* LEFT */}
          <div className="flex flex-col gap-2 ">
            <div className="flex flex-row items-center gap-4">
              <CardTitle className="text-md flex items-center gap-2">
                <AssetBadge
                  logo={logos?.[item?.strategy?.symbol]}
                  timeframe={item.strategy.timeframe}
                  symbol={item?.strategy?.symbol}
                  hideLogo={!logos?.[item?.strategy?.symbol]}
                />
              </CardTitle>
              {symbols_list?.length && (
                <div className="flex flex-col gap-2 items-start font-semibold ">
                  <p className="text-[10px] text-muted-foreground">
                    {"Signal Asset" + (symbols_list.length > 1 ? "s" : "")}
                  </p>
                  <div className="flex gap-2">
                    <Carousel
                      opts={{ slidesToScroll: 2, loop: true }}
                      plugins={[
                        Autoplay({
                          delay: 2000,
                          stopOnInteraction: false,
                        }),
                      ]}
                      className="w-full max-w-[120px] sm:max-w-[180px]"
                    >
                      <CarouselContent>
                        {symbols_list?.map((sym: string, idx: number) => (
                          <CarouselItem
                            key={idx}
                            className={cn(
                              "flex justify-center items-center basis-1/2 ",
                              symbols_list.length < 2 && "basis-full",
                            )}
                          >
                            <AssetBadge
                              key={idx}
                              size={25}
                              logo={logos?.[sym]}
                              symbol={sym}
                              hideLogo={!logos?.[sym]}
                            />
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </Carousel>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT STATS */}
          <div className="  flex items-end">
            {/* WIN */}
            <div className="flex flex-col">
              <div
                className={cn(
                  "font-semibold text-xl sm:text-2xl lg:text-3xl",
                  item?.backtest_data?.win >= 75
                    ? "text-[--marketplace-green]"
                    : item?.backtest_data?.win >= 50
                      ? "text-[--accent-secondary]"
                      : "text-[--marketplace-red]",
                )}
              >
                {formatToDecimalPlaces(item?.backtest_data?.win, 0)}%
              </div>
              <div className="text-[11px] text-[var(--marketplace-grey)] font-semibold whitespace-break-spaces">
                {t("Average Win")}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <div className="flex  justify-between items-center px-2">
        {category && (
          <div className="bg-[--accent-primary] rounded-xl text-[10px] px-2 py-[2px] text-white w-fit">
            {category}
          </div>
        )}
        <div className="flex items-center gap-4 justify-end ">
          {/* ACTIONS */}
          {actionList.length > 0 && (
            <div className="flex flex-col">
              <div className="flex flex-wrap gap-2 text-sm font-semibold">
                {actionList.map((each, i) => {
                  switch (actionOptions[each]) {
                    case "Long":
                      return (
                        <div
                          key={i}
                          className="text-green-foreground flex items-center"
                        >
                          Long <CgArrowTopRight />
                        </div>
                      );
                    case "Short":
                      return (
                        <div
                          key={i}
                          className="text-red-foreground flex items-center"
                        >
                          Short <CgArrowBottomRight />
                        </div>
                      );
                    case "Smart Buy":
                      return (
                        <div
                          key={i}
                          className="text-green-foreground flex items-center"
                        >
                          Smart Buy <FaArrowTrendUp />
                        </div>
                      );
                    case "Smart Sell":
                      return (
                        <div
                          key={i}
                          className="text-red-foreground flex items-center"
                        >
                          Smart Sell <FaArrowTrendDown />
                        </div>
                      );
                  }
                })}
              </div>
              <div className="text-[11px] text-[var(--marketplace-grey)] font-semibold">
                {t("Actions")}
              </div>
            </div>
          )}

          {maxTakeProfit >= 0 && (
            <div className="flex flex-col ">
              <div className="text-green-foreground font-semibold">
                {maxTakeProfit}%
              </div>
              <div className="text-[11px] text-[var(--marketplace-grey)] font-semibold">
                {t("Take Profit")}
              </div>
            </div>
          )}

          {stopLoss >= 0 && (
            <div className="flex flex-col ">
              <div className="text-red-foreground font-semibold">
                {stopLoss}%
              </div>
              <div className="text-[11px] text-[var(--marketplace-grey)] font-semibold">
                {t("Stop Loss")}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CONTENT */}
      <CardContent className="p-4 pt-2 flex flex-col gap-3 ">
        <div className="border-b pb-4">
          <div className="w-full">
            {/* <Carousel opts={{ align: "start", dragFree: true }}>
              <CarouselContent className="-ml-2">
                {badges?.map((b, i) => (
                  <CarouselItem key={i} className="pl-2 basis-auto">
                    <Badge variant="outline">{b}</Badge>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel> */}
          </div>

          {/* STATS */}
          <div className="flex flex-wrap text-xs gap-6 mt-4">
            <div className="flex flex-col gap-1">
              <HintLabel
                label={t("Drawdown")}
                className="text-[var(--marketplace-grey)] font-semibold"
                hint={t(
                  "How much your strategy drops from its highest point to its lowest to measure risk - smaller drops mean less risk, bigger drops mean more risk.",
                )}
              />
              <div
                className={
                  item?.backtest_data?.dd > 0
                    ? "text-[var(--foreground-green)]"
                    : "text-[var(--foreground-red)]"
                }
              >
                <PriceLabel value={item?.backtest_data?.dd} placeHolder="N/A" />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="text-[var(--marketplace-grey)] font-semibold">
                {t("Sharpe Ratio")}
              </div>
              <div>{item?.backtest_data?.sr}</div>
            </div>

            {/* <div className="flex flex-col gap-1">
              <div className="text-[var(--marketplace-grey)] font-semibold">
                {t("Copier Count")}
              </div>
              <div>{item?.meta_data?.copiers_count}</div>
            </div> */}
          </div>
        </div>
      </CardContent>

      <CardFooter className="flex items-center gap-3 pb-4  justify-between">
        <StrategyName
          name={item?.strategy?.strategy_name}
          className="flex-1 min-w-0"
        />
        <div className="flex gap-4 items-center flex-1  justify-end">
          {showExplain && (
            <Button
              className="text-sm px-0 font-bold hover:bg-transparent cursor-pointer text-muted-foreground hover:text-[--accent-primary]"
              variant="ghost"
              onClick={() =>
                onExplain({
                  nodes,
                  edges,
                  symbol: item.strategy.symbol,
                  timeframe: item.strategy.timeframe,
                  strategy_id: item?.strategy?.id,
                })
              }
            >
              <div className="flex items-center gap-1">
                <FaLightbulb />
                {t("Explainer")}
              </div>
            </Button>
          )}

          <Button
            className="text-sm px-0 font-bold hover:bg-transparent cursor-pointer text-muted-foreground hover:text-[--accent-primary]"
            variant="ghost"
            onClick={() => {
              onView({
                nodes,
                edges,
                symbol: item.strategy.symbol,
                timeframe: item.strategy.timeframe,
                broker_id: item.strategy.broker_id || "4",
              });
            }}
          >
            <div className="flex items-center gap-1">
              <Eye className="w-[15px]" />
              {t("View")}
            </div>
          </Button>

          <Button
            type="button"
            loading={isCurrentPriceLoading}
            variant="ghost"
            className="text-sm px-0 font-bold hover:bg-transparent cursor-pointer text-muted-foreground hover:text-[--accent-primary]"
            onClick={() => onCopy(item)}
          >
            <div className="flex items-center gap-1">
              <Layers2 className="w-[15px]" />
              {t("Copy")}
            </div>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
